import { useCallback, useEffect, useRef, useState } from 'react';

interface Point { x: number; y: number }
interface SignaturePadProps {
  value?: string;
  onChange: (path: string) => void;
  disabled?: boolean;
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 240;

function strokeToPath(points: Point[]) {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const point = points[0];
    return `M ${point.x.toFixed(1)} ${point.y.toFixed(1)} l 0.1 0`;
  }
  if (points.length === 2) {
    return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)} L ${points[1].x.toFixed(1)} ${points[1].y.toFixed(1)}`;
  }

  const parts = [`M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`];
  for (let index = 1; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const midpoint = { x: (current.x + next.x) / 2, y: (current.y + next.y) / 2 };
    parts.push(`Q ${current.x.toFixed(1)} ${current.y.toFixed(1)} ${midpoint.x.toFixed(1)} ${midpoint.y.toFixed(1)}`);
  }
  const last = points.at(-1)!;
  parts.push(`L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`);
  return parts.join(' ');
}

export function SignaturePad({ value = '', onChange, disabled = false }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const activePointerRef = useRef<number | null>(null);
  const currentStrokeRef = useRef<Point[]>([]);
  const committedPathRef = useRef(value);
  const [confirmed, setConfirmed] = useState(Boolean(value));

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#e7e7e3';
    context.lineWidth = 4;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    const committedPath = committedPathRef.current;
    if (committedPath) context.stroke(new Path2D(committedPath));

    const livePath = strokeToPath(currentStrokeRef.current);
    if (livePath) context.stroke(new Path2D(livePath));
  }, []);

  useEffect(() => {
    committedPathRef.current = value;
    draw();
  }, [draw, value]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(canvas.width, (event.clientX - rect.left) * canvas.width / rect.width)),
      y: Math.max(0, Math.min(canvas.height, (event.clientY - rect.top) * canvas.height / rect.height)),
    };
  };

  const appendPointerSamples = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const samples = typeof event.nativeEvent.getCoalescedEvents === 'function'
      ? event.nativeEvent.getCoalescedEvents()
      : [event.nativeEvent];
    for (const sample of samples) {
      const canvas = canvasRef.current;
      if (!canvas) continue;
      const rect = canvas.getBoundingClientRect();
      const candidate = {
        x: Math.max(0, Math.min(canvas.width, (sample.clientX - rect.left) * canvas.width / rect.width)),
        y: Math.max(0, Math.min(canvas.height, (sample.clientY - rect.top) * canvas.height / rect.height)),
      };
      const last = currentStrokeRef.current.at(-1);
      if (!last || Math.hypot(candidate.x - last.x, candidate.y - last.y) >= 0.8) currentStrokeRef.current.push(candidate);
    }
  };

  const finishStroke = (event?: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    if (event && activePointerRef.current !== event.pointerId) return;

    drawingRef.current = false;
    activePointerRef.current = null;
    const stroke = strokeToPath(currentStrokeRef.current);
    currentStrokeRef.current = [];
    if (stroke) {
      const nextPath = committedPathRef.current ? `${committedPathRef.current} ${stroke}` : stroke;
      committedPathRef.current = nextPath;
      onChange(nextPath);
    }
    draw();
  };

  const rewrite = () => {
    drawingRef.current = false;
    activePointerRef.current = null;
    currentStrokeRef.current = [];
    committedPathRef.current = '';
    setConfirmed(false);
    onChange('');
    draw();
  };

  const isLocked = confirmed || disabled;

  return <div className={`signature-pad ${confirmed ? 'is-confirmed' : ''}`}>
    <canvas
      ref={canvasRef}
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      aria-label={confirmed ? '已確認的手寫簽名' : '手寫簽名區，可連續書寫多段線條'}
      aria-disabled={isLocked}
      onPointerDown={(event) => {
        if (isLocked || drawingRef.current) return;
        drawingRef.current = true;
        activePointerRef.current = event.pointerId;
        currentStrokeRef.current = [pointFromEvent(event)];
        event.currentTarget.setPointerCapture(event.pointerId);
        draw();
      }}
      onPointerMove={(event) => {
        if (!drawingRef.current || isLocked || activePointerRef.current !== event.pointerId) return;
        appendPointerSamples(event);
        draw();
      }}
      onPointerUp={finishStroke}
      onPointerCancel={finishStroke}
      onLostPointerCapture={finishStroke}
    />
    <div className="signature-actions">
      <span className="signature-status" aria-live="polite">{confirmed ? '簽名已鎖定' : value ? '可繼續書寫' : '請在上方簽名'}</span>
      <button className="text-button" type="button" disabled={disabled || !value} onClick={rewrite}>重寫</button>
      <button className="text-button accent" type="button" disabled={disabled || !value || confirmed} onClick={() => setConfirmed(true)}>{confirmed ? '已確認' : '確認簽名'}</button>
    </div>
  </div>;
}
